import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Close as CloseIcon, ContentCopy as CopyIcon, Check as CheckIcon } from '@mui/icons-material';
import './IntelPopup.css';

/**
 * Shared "intel card" map popup used by COP and Pathfinder.
 *
 * Styled after the commercial Signals popup: dark slate card, colored accent
 * rail on the left, monospace title, uppercase muted labels with bright
 * values, a monospace coordinate footer and uppercase action links.
 *
 * Wrap the react-map-gl <Popup> with `className={INTEL_POPUP_CLASS}` and
 * `closeButton={false}` — the card draws its own close button and, after
 * mount, strips Mapbox's white `.mapboxgl-popup-content` container and tints
 * the tip with inline !important styles (mapbox-gl.css is imported per map
 * component and would otherwise win the cascade).
 */

export const INTEL_POPUP_CLASS = 'intel-popup';

const TIP_SIDES = ['top', 'bottom', 'left', 'right'] as const;

/** Strip Mapbox popup chrome around `el` so only the card is visible. Returns false if not attached yet. */
function paintIntelPopupChrome(el: HTMLElement | null): boolean {
  const root = el?.closest('.mapboxgl-popup') as HTMLElement | null;
  if (!root) return false;
  const content = root.querySelector('.mapboxgl-popup-content') as HTMLElement | null;
  if (content) {
    content.style.setProperty('background', 'transparent', 'important');
    content.style.setProperty('background-color', 'transparent', 'important');
    content.style.setProperty('padding', '0', 'important');
    content.style.setProperty('box-shadow', 'none', 'important');
    content.style.setProperty('border-radius', '0', 'important');
    content.style.setProperty('border', '0', 'important');
  }
  const tip = root.querySelector('.mapboxgl-popup-tip') as HTMLElement | null;
  if (tip) {
    // Mapbox draws the tip with CSS borders; the side opposite the anchor is the colored one.
    const anchor = Array.from(root.classList).find((c) => c.startsWith('mapboxgl-popup-anchor-')) || '';
    const colored = anchor.includes('bottom') ? 'top' : anchor.includes('top') ? 'bottom' : anchor.includes('left') ? 'right' : anchor.includes('right') ? 'left' : 'top';
    for (const side of TIP_SIDES) {
      tip.style.setProperty(`border-${side}-color`, side === colored ? INTEL_COLORS.bg : 'transparent', 'important');
    }
  }
  const close = root.querySelector('.mapboxgl-popup-close-button') as HTMLElement | null;
  if (close) close.style.setProperty('display', 'none', 'important');
  return true;
}

export const INTEL_COLORS = {
  bg: '#161b22',
  bgRaised: '#1f2630',
  border: 'rgba(255,255,255,0.06)',
  text: '#e6edf3',
  textMuted: '#8b949e',
  textDim: '#6e7681',
  green: '#4ade80',
  blue: '#58a6ff',
  amber: '#f0b429',
  red: '#f85149',
  purple: '#bc8cff',
  teal: '#2dd4bf',
} as const;

export const INTEL_MONO = "'JetBrains Mono', 'Roboto Mono', 'SFMono-Regular', Menlo, Consolas, monospace";

export type IntelRow = {
  label: string;
  value: React.ReactNode;
  /** Render the value in monospace. */
  mono?: boolean;
  /** Override value color (e.g. a status color). */
  color?: string;
  /** Hide when the value is null/undefined/'' (default true). */
  hideEmpty?: boolean;
};

export type IntelAction = {
  label: string;
  onClick?: () => void;
  href?: string;
  /** Default uses the card accent. */
  color?: string;
  disabled?: boolean;
  title?: string;
  icon?: React.ReactNode;
};

export type IntelPopupCardProps = {
  /** Primary identifier (monospace, accent colored). */
  title: React.ReactNode;
  /** Optional secondary line under the title. */
  subtitle?: React.ReactNode;
  /** Small outlined tag to the right of the title (e.g. device type). */
  chip?: React.ReactNode;
  /** Accent color for rail / title / actions. */
  accent?: string;
  rows?: IntelRow[];
  /** Media strip (e.g. Street View thumbnail) rendered between the header and the rows. */
  media?: React.ReactNode;
  /** Coordinate footer (lat/lon, optional MGRS) with copy buttons. */
  coords?: { lat: number; lon: number; mgrs?: string | null; precision?: number } | null;
  actions?: IntelAction[];
  /** Arbitrary extra body (rendered below rows, above coords). */
  children?: React.ReactNode;
  onClose?: () => void;
  minWidth?: number;
  maxWidth?: number;
  /** Dim the card while data resolves. */
  loading?: boolean;
};

const isEmpty = (v: React.ReactNode) =>
  v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));

const CopyButton: React.FC<{ text: string; label?: string }> = ({ text, label }) => {
  const [done, setDone] = useState(false);
  const copy = useCallback(() => {
    try {
      void navigator.clipboard.writeText(text);
      setDone(true);
      window.setTimeout(() => setDone(false), 1200);
    } catch {
      /* clipboard unavailable */
    }
  }, [text]);
  return (
    <Tooltip title={done ? 'Copied' : label || 'Copy'} placement="top">
      <IconButton
        size="small"
        onClick={copy}
        aria-label={label || `Copy ${text}`}
        sx={{ p: '2px', ml: 0.5, color: done ? INTEL_COLORS.green : INTEL_COLORS.textDim, '&:hover': { color: INTEL_COLORS.text } }}
      >
        {done ? <CheckIcon sx={{ fontSize: 13 }} /> : <CopyIcon sx={{ fontSize: 13 }} />}
      </IconButton>
    </Tooltip>
  );
};

/** Small outlined uppercase tag (device type, category, ...). */
export const IntelChip: React.FC<{ children: React.ReactNode; color?: string; filled?: boolean }> = ({
  children,
  color = INTEL_COLORS.textMuted,
  filled = false,
}) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: 0.75,
      py: '1px',
      borderRadius: '4px',
      border: `1px solid ${filled ? color : 'rgba(255,255,255,0.22)'}`,
      bgcolor: filled ? color : 'transparent',
      color: filled ? '#0b0f14' : INTEL_COLORS.text,
      fontSize: '0.62rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      lineHeight: 1.6,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Box>
);

export const IntelPopupCard: React.FC<IntelPopupCardProps> = ({
  title,
  subtitle,
  chip,
  accent = INTEL_COLORS.green,
  rows = [],
  media,
  coords,
  actions = [],
  children,
  onClose,
  minWidth = 232,
  maxWidth = 320,
  loading = false,
}) => {
  const visibleRows = rows.filter((r) => (r.hideEmpty ?? true) ? !isEmpty(r.value) : true);
  const precision = coords?.precision ?? 5;
  const llStr = coords ? `${coords.lat.toFixed(precision)}, ${coords.lon.toFixed(precision)}` : '';
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Re-run on every render: Mapbox swaps the anchor class as the popup moves.
  // react-map-gl attaches our container to `.mapboxgl-popup` in *its* effect,
  // which runs after this one on first mount — so retry over the next frames
  // until the chrome exists, otherwise Mapbox's white box flashes.
  useLayoutEffect(() => {
    if (paintIntelPopupChrome(rootRef.current)) return undefined;
    let attempts = 0;
    let raf = 0;
    const tick = () => {
      if (paintIntelPopupChrome(rootRef.current) || ++attempts > 20) return;
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  });

  return (
    <Box
      ref={rootRef}
      sx={{
        position: 'relative',
        minWidth,
        maxWidth,
        bgcolor: INTEL_COLORS.bg,
        color: INTEL_COLORS.text,
        borderRadius: '10px',
        borderLeft: `3px solid ${accent}`,
        boxShadow: '0 12px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.05)',
        px: 1.75,
        pt: 1.25,
        pb: actions.length ? 1 : 1.25,
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        opacity: loading ? 0.75 : 1,
        transition: 'opacity 150ms',
        '& *': { boxSizing: 'border-box' },
      }}
    >
      {onClose && (
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close"
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            p: '2px',
            color: INTEL_COLORS.textDim,
            '&:hover': { color: INTEL_COLORS.text, bgcolor: 'rgba(255,255,255,0.06)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: onClose ? 3 : 0, mb: subtitle ? 0.25 : 1 }}>
        <Box
          sx={{
            fontFamily: INTEL_MONO,
            fontWeight: 700,
            fontSize: '0.86rem',
            color: accent,
            letterSpacing: '0.01em',
            lineHeight: 1.3,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
          }}
          title={typeof title === 'string' ? title : undefined}
        >
          {title}
        </Box>
        {chip != null && (typeof chip === 'string' ? <IntelChip>{chip}</IntelChip> : chip)}
      </Box>
      {subtitle && (
        <Box sx={{ fontSize: '0.72rem', color: INTEL_COLORS.textMuted, mb: 1, lineHeight: 1.4 }}>{subtitle}</Box>
      )}

      {media && <Box sx={{ mb: visibleRows.length ? 1.25 : 0 }}>{media}</Box>}

      {/* Rows */}
      {visibleRows.length > 0 && (
        <Box
          component="dl"
          sx={{
            m: 0,
            display: 'grid',
            gridTemplateColumns: 'minmax(58px, auto) 1fr',
            columnGap: 1.5,
            rowGap: 0.6,
            alignItems: 'baseline',
          }}
        >
          {visibleRows.map((row, i) => (
            <React.Fragment key={`${row.label}-${i}`}>
              <Box
                component="dt"
                sx={{
                  fontSize: '0.64rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: INTEL_COLORS.textMuted,
                  whiteSpace: 'nowrap',
                  lineHeight: 1.5,
                }}
              >
                {row.label}
              </Box>
              <Box
                component="dd"
                sx={{
                  m: 0,
                  fontSize: '0.82rem',
                  fontWeight: 500,
                  color: row.color || INTEL_COLORS.text,
                  fontFamily: row.mono ? INTEL_MONO : 'inherit',
                  lineHeight: 1.5,
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                }}
              >
                {row.value}
              </Box>
            </React.Fragment>
          ))}
        </Box>
      )}

      {children && <Box sx={{ mt: visibleRows.length ? 1 : 0 }}>{children}</Box>}

      {/* Coordinates */}
      {coords && (
        <Box sx={{ mt: 1.25, pt: 1, borderTop: `1px solid ${INTEL_COLORS.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', fontFamily: INTEL_MONO, fontSize: '0.72rem', color: INTEL_COLORS.textMuted }}>
            <span>{llStr}</span>
            <CopyButton text={llStr} label="Copy lat/lon" />
          </Box>
          {coords.mgrs && (
            <Box sx={{ display: 'flex', alignItems: 'center', fontFamily: INTEL_MONO, fontSize: '0.72rem', color: INTEL_COLORS.textMuted, mt: 0.25 }}>
              <span>{coords.mgrs}</span>
              <CopyButton text={coords.mgrs} label="Copy MGRS" />
            </Box>
          )}
        </Box>
      )}

      {/* Actions */}
      {actions.length > 0 && (
        <Box
          sx={{
            mt: coords ? 1 : 1.25,
            pt: 1,
            borderTop: `1px solid ${INTEL_COLORS.border}`,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          {actions.map((a) => {
            const color = a.disabled ? INTEL_COLORS.textDim : a.color || accent;
            const sx = {
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.68rem',
              fontWeight: 800,
              letterSpacing: '0.12em',
              textTransform: 'uppercase' as const,
              color,
              textDecoration: 'none',
              cursor: a.disabled ? 'default' : 'pointer',
              background: 'none',
              border: 0,
              p: 0,
              fontFamily: 'inherit',
              '&:hover': a.disabled ? {} : { filter: 'brightness(1.2)', textDecoration: 'underline' },
            };
            return a.href ? (
              <Box
                key={a.label}
                component="a"
                href={a.disabled ? undefined : a.href}
                target="_blank"
                rel="noopener noreferrer"
                title={a.title}
                sx={sx}
                onClick={(e: React.MouseEvent) => {
                  if (a.disabled) e.preventDefault();
                  a.onClick?.();
                }}
              >
                {a.icon}
                {a.label}
              </Box>
            ) : (
              <Box key={a.label} component="button" type="button" disabled={a.disabled} title={a.title} onClick={a.onClick} sx={sx}>
                {a.icon}
                {a.label}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

/** Title-case a slug-ish string: "pan_tilt_zoom" → "Pan Tilt Zoom". */
export function humanize(value: unknown): string {
  if (value == null) return '';
  return String(value)
    .replace(/[_:-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** "3 min ago" / "just now" from a minutes-ago number. */
export function agoLabel(minutesAgo: number | null | undefined): string {
  if (minutesAgo == null || !Number.isFinite(minutesAgo)) return 'Unknown';
  if (minutesAgo < 1) return 'Just now';
  if (minutesAgo < 60) return `${Math.round(minutesAgo)} min ago`;
  const h = Math.floor(minutesAgo / 60);
  const m = Math.round(minutesAgo % 60);
  if (h < 24) return m ? `${h}h ${m}m ago` : `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** 0-360 → "90° E". */
export function bearingLabel(deg: number | null | undefined): string {
  if (deg == null || !Number.isFinite(deg)) return '';
  const d = ((deg % 360) + 360) % 360;
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return `${Math.round(d)}° ${dirs[Math.round(d / 45) % 8]}`;
}

export default IntelPopupCard;
